import { DocketDisplayItem, DocketDisplayItemType } from '../common/types';
import { LayoutStrategy } from '@oolio-group/domain';
import { DEFAULT_ROWS_PER_PAGE } from '../common/constants';

export interface Column {
  column: number;
  rows: {
    row: number;
    items: DocketDisplayItem[];
  }[];
}

export interface PaginatedDocketItems {
  page: number;
  columns: Column[];
}
/**
 *
 * @returns if the current docket is overflowing the column or not
 */
export const isOverflowingDocket = (
  items: DocketDisplayItem[],
  currentIndex: number,
  colHeight: number,
  currentHeight: number,
  continueHeight: number,
  scale: (n: number) => number,
  isGridLayout?: boolean,
): boolean => {
  const currentItem = items[currentIndex];
  /**
   * For grid layout, we allow flexible placement for most headers instead of forcing
   * them to group with their first item. This prevents "lonely headers" (like tags or
   * title bars) from appearing alone in their own column when they could fit with other content.
   *
   * Only semantically important grouping headers (COURSE, SEAT_NUMBER, CATEGORY) should
   * use the strict "header + first item" grouping logic, as these headers logically
   * organize related content.
   *
   * Other headers (DOCKET_HEADER, TRAINING_DOCKET, DOCKET_TITLE_BAR, tags) are more
   * informational and can be placed flexibly to optimize space usage in grid layout.
   */
  if (
    currentItem.type !== DocketDisplayItemType.COURSE &&
    currentItem.type !== DocketDisplayItemType.SEAT_NUMBER &&
    currentItem.type !== DocketDisplayItemType.CATEGORY &&
    isGridLayout
  ) {
    return false;
  }
  if (
    currentItem.type === DocketDisplayItemType.DOCKET_HEADER ||
    currentItem.type === DocketDisplayItemType.COURSE ||
    currentItem.type === DocketDisplayItemType.TRAINING_DOCKET ||
    currentItem.type === DocketDisplayItemType.DOCKET_TITLE_BAR ||
    currentItem.type === DocketDisplayItemType.CATEGORY ||
    currentItem.type === DocketDisplayItemType.SEAT_NUMBER
  ) {
    const totalEligibleDocketHeight = getDocketHeightWithFirstItem(
      items,
      currentIndex,
    );
    /** We are doing -2 here because we want to account for the current item
    is header we want to account for next two items
    */
    const hasRemainingItems = currentIndex < items.length - 2;
    const effectiveContinueHeight = hasRemainingItems ? continueHeight : 0;
    if (
      scale(totalEligibleDocketHeight) +
        effectiveContinueHeight +
        currentHeight >
      colHeight
    ) {
      return true;
    }
  }
  return false;
};

/**
 *
 * @returns the total height of the docket including the first item
 */
const getDocketHeightWithFirstItem = (
  items: DocketDisplayItem[],
  currentIndex: number,
): number => {
  let height = items[currentIndex].height;
  for (let i = currentIndex + 1; i < items.length; i++) {
    height += items[i].height;

    if (items[i].type === DocketDisplayItemType.ITEM) {
      break;
    }
  }
  return height;
};

/**
 *
 * @param layoutStrategy - The layout strategy to determine the columns rows per page.
 * @returns number of columns per page based on the layout strategy.
 */
export const getDefaultRowsPerPage = (
  layoutStrategy = LayoutStrategy.SINGLE_ROW,
) => {
  return DEFAULT_ROWS_PER_PAGE[layoutStrategy];
};
